// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api";
import { getCurrentUser } from "aws-amplify/auth";
import { downloadData } from "aws-amplify/storage";
import {
  listDatasets,
  getDataset,
  datasetsByOrganizationIdAndSlug,
  dataSetRecordsByDatasetIdAndTimeValue
} from "../graphql/queries";
import { getDataSetRecordSimple } from "../graphql/customQueries";
import { getMyOrganizations } from "./userSlice";
import {
  createDataset,
  updateDataset
} from "../graphql/mutations";

const client = generateClient();

// Helper function to calculate time range from CSV content
const calculateTimeRange = (csvContent) => {
  const lines = csvContent.trim().split("\n");
  const headers = lines[0].toLowerCase().split(",").map(h => h.trim());
  const timestampIndex = headers.indexOf("timestamp");
  
  if (timestampIndex === -1 || lines.length <= 1) {
    return null;
  }

  const timestamps = [];
  for (let i = 1; i < lines.length; i++) {
    const row = lines[i].split(",");
    if (row.length > timestampIndex) {
      const timestamp = row[timestampIndex].trim();
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        timestamps.push(date.getTime());
      }
    }
  }
  
  if (timestamps.length === 0) {
    return null;
  }

  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);
  return JSON.stringify({
    startDate: new Date(minTime).toISOString(),
    endDate: new Date(maxTime).toISOString()
  });
};

const initialState = {
  loading: "idle",
  error: null,
  personalDatasets: {},
  organizationDatasets: {},
  selectedDataset: null,
};

// Newest fetchDatasets call per argument set, so a slow older one cannot overwrite a newer result for the same question (the page refetches once the user's orgs arrive). Callers asking something else (a project widget scoped to one org) never supersede each other
const latestFetchIds: Record<string, number> = {};
let fetchCounter = 0;

// Fetch all datasets (personal and organization)
export const fetchDatasets = createAsyncThunk(
  "datasets/fetchAll",
  async ({ userId, organizationIds }: { userId: string; organizationIds: string[] }, thunkAPI) => {
    const fetchKey = `${userId}|${[...organizationIds].sort().join(",")}`;
    const fetchId = ++fetchCounter;
    latestFetchIds[fetchKey] = fetchId;
    // A newer call for the same question has started: its result is the one the store should keep, and a failure here is no longer news
    const isSuperseded = () => latestFetchIds[fetchKey] !== fetchId;
    try {
      // Fetch all datasets first
      const { data } = await client.graphql({
        query: listDatasets,
      });

      // Filter out soft-deleted datasets
      const allDatasets = data.listDatasets.items.filter(
        dataset => !dataset.deletedAt
      );
      
      // Separate personal and organization datasets
      const personal = {};
      const organization = {};
      
      allDatasets.forEach(dataset => {
        // Any of the user's orgs, not just one — the create modal offers every org in state.user.organizations
        const belongsToOrg =
          Boolean(dataset.organizationId) && organizationIds.includes(dataset.organizationId);
        const belongsToUser =
          dataset.userId === userId || dataset.owner === userId;

        if (belongsToOrg) {
          organization[dataset.id] = dataset;
        } else if (belongsToUser || !dataset.organizationId) {
          personal[dataset.id] = dataset;
        }
      });

      if (isSuperseded()) return { superseded: true };

      return {
        personal,
        organization
      };
    } catch (error) {
      console.error("Error fetching datasets:", error);
      if (isSuperseded()) return { superseded: true };
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Fetch for the signed-in user across every org they belong to, so a secondary caller never narrows the shared store (loads the org list first when it is missing)
export const fetchDatasetsForCurrentUser = createAsyncThunk(
  "datasets/fetchForCurrentUser",
  async (_: void, thunkAPI) => {
    const { userId } = await getCurrentUser();
    if (!(thunkAPI.getState() as any).user?.organizations?.loaded) {
      await thunkAPI.dispatch(getMyOrganizations());
    }
    const organizations = (thunkAPI.getState() as any).user?.organizations?.data || {};
    return thunkAPI.dispatch(fetchDatasets({ userId, organizationIds: Object.keys(organizations) })).unwrap();
  }
);

// Create a new dataset
export const createDatasetAction = createAsyncThunk(
  "datasets/create",
  async (input, thunkAPI) => {
    try {
      console.log("Creating dataset with input:", input);
      console.log("Extracted cognitoIdentityId:", input.cognitoIdentityId);
      
      // Extract cognitoIdentityId for S3 path (not part of GraphQL)
      const { cognitoIdentityId, ...graphqlInput } = input;
      const columnsArray = Array.isArray(input.columns)
        ? input.columns
        : typeof input.columns === "string" && input.columns.length
          ? (() => {
              try {
                const parsed = JSON.parse(input.columns);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            })()
          : [];

      // Add routeLong based on organizationId or personal
      const routeLong = graphqlInput.organizationId
        ? `organizationprivate/${graphqlInput.organizationId}/datasets/${graphqlInput.id}/`
        : `private/${cognitoIdentityId}/datasets/${graphqlInput.id}/`;

      // Build dataset input, excluding null organizationId
      const datasetInput = {
        ...graphqlInput,
        routeLong,
        fileFormat: graphqlInput.fileFormat || "CSV",
        columns: columnsArray.length ? JSON.stringify(columnsArray) : null,
        recordCount: 0,
        fileSize: 0,
        lastUpdated: new Date().toISOString(),
        // Uploads to a historical dataset are curated to Parquet from the start (stage 6 of the ingest plan),
        // so the chart and the AI Analyst read one series across every upload. Editable in Edit Dataset.
        ...(graphqlInput.datasetType === "HISTORICAL" ? { curationMode: "PARQUET" } : {}),
      };
      
      // Remove null fields to avoid GraphQL errors
      if (!datasetInput.organizationId) {
        delete datasetInput.organizationId;
      }
      if (!datasetInput.userId) {
        delete datasetInput.userId;
      }
      
      // Ensure at least one of userId or organizationId is present
      if (!datasetInput.userId && !datasetInput.organizationId) {
        throw new Error("Either userId or organizationId must be provided");
      }

      console.log("Dataset input for GraphQL:", datasetInput);

      // Create the dataset record in DynamoDB
      const { data } = await client.graphql({
        query: createDataset,
        variables: { input: datasetInput },
      });

      console.log("GraphQL response:", data);

      // No placeholder file: data arrives through the ingest pipeline as uploads (datasets from before stage 6
      // may still hold a legacy data.csv, which the readers below fall back to)
      return data.createDataset;
    } catch (error) {
      console.error("Error creating dataset:", error);
      console.error("Full error object:", error);
      
      // Extract error message
      let errorMessage = "Failed to create dataset";
      if (error.errors && error.errors.length > 0) {
        errorMessage = error.errors[0].message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Update a dataset
export const updateDatasetAction = createAsyncThunk(
  "datasets/update",
  async (input, thunkAPI) => {
    try {
      // Remove any __typename fields
      const cleanInput = Object.keys(input).reduce((acc, key) => {
        if (key !== "__typename") {
          acc[key] = input[key];
        }
        return acc;
      }, {});

      const { data } = await client.graphql({
        query: updateDataset,
        variables: { input: cleanInput },
      });
      return data.updateDataset;
    } catch (error) {
      console.error("Error updating dataset:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Delete a dataset (soft delete)
export const deleteDatasetAction = createAsyncThunk(
  "datasets/delete",
  // Annotated so typed callers see the argument — the file's @ts-nocheck would infer `void`
  async ({ id }: { id: string }, thunkAPI) => {
    try {
      // Perform soft delete by setting deletedAt timestamp
      await client.graphql({
        query: updateDataset,
        variables: {
          input: {
            id,
            deletedAt: new Date().toISOString()
          }
        },
      });

      // Note: We don't delete S3 files immediately A separate cleanup process can handle that later This allows for recovery if needed
      
      return id;
    } catch (error) {
      console.error("Error soft-deleting dataset:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Check if slug is unique within organization
export const checkSlugUniqueness = createAsyncThunk(
  "datasets/checkSlug",
  async ({ organizationId, slug }, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: datasetsByOrganizationIdAndSlug,
        variables: { organizationId, slug: { eq: slug } },
      });

      const items = data.datasetsByOrganizationIdAndSlug?.items || [];
      return { isUnique: items.length === 0, slug };
    } catch (error) {
      console.error("Error checking slug uniqueness:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Fetch dataset historical data for visualization
export const fetchDatasetHistoricalData = createAsyncThunk(
  "datasets/fetchHistoricalData",
  async ({ datasetId, days = null, startDate = null, endDate = null, recordId = null }: { datasetId: string; days?: number | null; startDate?: string | null; endDate?: string | null; recordId?: string | null }, thunkAPI) => {
    try {
      // Get the dataset metadata
      const { data } = await client.graphql({
        query: getDataset,
        variables: { id: datasetId },
      });

      const dataset = data.getDataset;
      let csvContent = '';
      let s3PathToDownload = null;

      // Determine which S3 path to download from
      if (recordId) {
        // Specific record requested - use simple query to avoid nested auth issues
        console.log('[fetchDatasetHistoricalData] Fetching record:', recordId);
        const { data: recordData } = await client.graphql({
          query: getDataSetRecordSimple,
          variables: { id: recordId },
        });
        console.log('[fetchDatasetHistoricalData] Record data:', recordData?.getDataSetRecord);
        if (recordData?.getDataSetRecord?.s3Path) {
          s3PathToDownload = recordData.getDataSetRecord.s3Path;
        }
      } else {
        // Query for the most recent DataSetRecord
        const { data: recordsData } = await client.graphql({
          query: dataSetRecordsByDatasetIdAndTimeValue,
          variables: {
            datasetId,
            sortDirection: 'DESC',
            limit: 1
          },
        });

        const records = recordsData?.dataSetRecordsByDatasetIdAndTimeValue?.items || [];
        if (records.length > 0) {
          s3PathToDownload = records[0].s3Path;
          console.log('Using latest DataSetRecord:', records[0].id, 'path:', s3PathToDownload);
        }
      }

      // If we have a path from DataSetRecord, download directly
      if (s3PathToDownload) {
        try {
          console.log('Downloading from DataSetRecord path:', s3PathToDownload);
          const downloadResult = await downloadData({
            path: s3PathToDownload,
          }).result;

          const blob = await downloadResult.body.blob();
          // Check if compressed
          if (s3PathToDownload.endsWith('.gz')) {
            const { decompressCSV } = await import('../utils/compressionUtils');
            csvContent = await decompressCSV(blob);
          } else {
            csvContent = await blob.text();
          }
        } catch (error) {
          console.error('Failed to download from DataSetRecord path:', error);
          return [];
        }
      } else {
        // Fallback for legacy datasets without DataSetRecords
        try {
          const s3PathCompressed = `${dataset.routeLong}data.csv.gz`;
          const downloadResult = await downloadData({
            path: s3PathCompressed,
          }).result;

          const blob = await downloadResult.body.blob();
          const { decompressCSV } = await import('../utils/compressionUtils');
          csvContent = await decompressCSV(blob);
        } catch (compressedError) {
          console.log('Compressed file not found, trying uncompressed...');

          // Try uncompressed version
          try {
            const s3PathUncompressed = `${dataset.routeLong}data.csv`;
            const downloadResult = await downloadData({
              path: s3PathUncompressed,
            }).result;
            csvContent = await downloadResult.body.text();
          } catch (uncompressedError) {
            console.error('Failed to download historical data (both compressed and uncompressed):', compressedError, uncompressedError);
            return [];
          }
        }
      }
      const lines = csvContent.trim().split("\n");
      
      if (lines.length <= 1) {
        return []; // No data rows
      }

      // Parse CSV headers - handle flexible column names
      const headers = lines[0].split(",").map(h => h.trim());
      const headersLower = headers.map(h => h.toLowerCase());
      
      // Find timestamp column - accept various common names
      let timestampIndex = -1;
      const timestampPatterns = [
        'datetime', 'timestamp', 'date_time', 'date', 'time', 
        'created_at', 'updated_at', 'recorded_at', 'ts'
      ];
      
      for (let i = 0; i < headersLower.length; i++) {
        const headerLower = headersLower[i];
        // Check for exact matches first
        if (timestampPatterns.includes(headerLower)) {
          timestampIndex = i;
          break;
        }
        // Then check for partial matches
        if (headerLower.includes('date') || headerLower.includes('time')) {
          timestampIndex = i;
          break;
        }
      }
      
      if (timestampIndex === -1) {
        console.error("CSV missing timestamp column. Headers:", headers);
        return [];
      }
      
      console.log(`Found timestamp column: '${headers[timestampIndex]}'`);
      console.log(`All columns:`, headers);

      // Parse data rows, keeping every column
      let filterStartDate, filterEndDate;
      
      if (startDate && endDate) {
        // Use provided date range
        filterStartDate = new Date(startDate);
        filterEndDate = new Date(endDate);
        // Extend end date to end of day
        filterEndDate.setHours(23, 59, 59, 999);
        console.log('Using custom date range:', filterStartDate, 'to', filterEndDate);
      } else if (days) {
        // Use days parameter
        const now = new Date();
        filterEndDate = now;
        filterStartDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        console.log('Using days filter:', days, 'days from', filterStartDate, 'to', filterEndDate);
      }
      
      const dataRows = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // Skip empty lines
        
        const row = line.split(",").map(cell => cell.trim());
        
        // Ensure row has the same number of columns as headers Fill missing columns with empty strings
        while (row.length < headers.length) {
          row.push('');
        }
        
        if (row.length > timestampIndex) {
          const timestamp = row[timestampIndex];
          if (!timestamp) continue; // Skip rows without timestamp
          
          // Check if timestamp is within range
          const recordDate = new Date(timestamp);
          let includeRecord = true;
          
          if (filterStartDate && filterEndDate) {
            includeRecord = !isNaN(recordDate.getTime()) && 
                           recordDate >= filterStartDate && 
                           recordDate <= filterEndDate;
          }
          
          if (includeRecord && !isNaN(recordDate.getTime())) {
            // Create object with all columns
            const dataPoint = {
              id: i,
              timestamp: timestamp,
            };
            
            // Add all other columns (excluding the timestamp column we already handled)
            headers.forEach((header, index) => {
              if (index !== timestampIndex) {
                const cellValue = index < row.length ? row[index] : '';
                // Handle empty values and parse numbers
                if (cellValue === '' || cellValue === null || cellValue === undefined) {
                  // For empty values, use null for better handling in charts
                  dataPoint[header] = null;
                } else {
                  // Try to parse as number, otherwise keep as string
                  const numValue = parseFloat(cellValue);
                  dataPoint[header] = !isNaN(numValue) ? numValue : cellValue;
                }
              }
            });
            
            dataRows.push(dataPoint);
          }
        }
      }

      // Sort by timestamp (oldest to newest)
      dataRows.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      console.log(`Filtered data: ${dataRows.length} records out of ${lines.length - 1} total`);
      if (dataRows.length > 0) {
        console.log('Date range in filtered data:', dataRows[0].timestamp, 'to', dataRows[dataRows.length - 1].timestamp);
      }
      
      return dataRows;
    } catch (error) {
      console.error("Error fetching dataset historical data:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Re-read one dataset after ingest changed it server-side (an upload finished), so the card and the chart's
// range see the new count and time range without a full list reload
export const fetchDatasetById = createAsyncThunk(
  "datasets/fetchOne",
  async ({ datasetId }, thunkAPI) => {
    try {
      const { data } = await client.graphql({ query: getDataset, variables: { id: datasetId } });
      if (!data?.getDataset) throw new Error("Dataset not found.");
      return data.getDataset;
    } catch (error) {
      console.error("Error fetching dataset:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// One page is not a count — walk every page before deciding what a dataset holds
const fetchAllDataSetRecords = async (datasetId) => {
  const all = [];
  let nextToken = null;
  do {
    const { data } = await client.graphql({
      query: dataSetRecordsByDatasetIdAndTimeValue,
      variables: { datasetId, sortDirection: "DESC", limit: 1000, nextToken },
    });
    const page = data?.dataSetRecordsByDatasetIdAndTimeValue;
    all.push(...(page?.items || []));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return all;
};

const parseJsonField = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

// Records already carry the counts ingest computed, so a recheck reads them rather than re-parsing S3.
// A dataset is the sum of its uploads: recordCount is total rows, timeRange the union, columns the
// union in first-seen order (the definitions ingest keeps as it goes; the recheck reconciles them after a race).
const statsFromDataSetRecords = (records) => {
  const fileSize = records.reduce((sum, r) => sum + (r.compressedSize || r.fileSize || 0), 0);
  let startDate = null;
  let endDate = null;
  const columns = [];
  const seen = new Set();
  // Oldest first, so the column order matches what ingest builds as uploads arrive
  for (const record of [...records].reverse()) {
    const range = parseJsonField(record.timeRange);
    if (range?.startDate && (!startDate || Date.parse(range.startDate) < Date.parse(startDate))) startDate = range.startDate;
    if (range?.endDate && (!endDate || Date.parse(range.endDate) > Date.parse(endDate))) endDate = range.endDate;
    const recordColumns = parseJsonField(record.columns);
    if (Array.isArray(recordColumns)) {
      for (const name of recordColumns) {
        if (typeof name === "string" && !seen.has(name)) {
          seen.add(name);
          columns.push(name);
        }
      }
    }
  }
  const observed = columns.filter((c) => c !== "timestamp");
  return {
    recordCount: records.reduce((sum, r) => sum + (r.recordCount || 0), 0),
    fileSize,
    ...(startDate && endDate ? { timeRange: JSON.stringify({ startDate, endDate }) } : {}),
    ...(observed.length ? { columns: JSON.stringify(["timestamp", ...observed]) } : {}),
  };
};

// Only for datasets predating DataSetRecords. Returns null when nothing could be read, so the caller can refuse to write.
const statsFromLegacyStorage = async (dataset) => {
  const { decompressCSV } = await import("../utils/compressionUtils");
  for (const path of [`${dataset.routeLong}data.csv.gz`, `${dataset.routeLong}data.csv`]) {
    try {
      const { body } = await downloadData({ path }).result;
      const csvContent = path.endsWith(".gz")
        ? await decompressCSV(await body.blob())
        : await body.text();
      const lines = csvContent.trim().split("\n");
      const timeRange = calculateTimeRange(csvContent);
      return {
        recordCount: Math.max(0, lines.length - 1), // drop the header
        fileSize: new Blob([csvContent]).size,
        ...(timeRange ? { timeRange } : {}),
      };
    } catch (error) {
      // Fall through to the next legacy path
    }
  }
  return null;
};

// Recheck what a dataset actually holds and repair its stored counts.
// Deliberately does not touch lastUpdated: rechecking reads the data, it does not change it.
export const refreshDatasetRecordCount = createAsyncThunk(
  "datasets/refreshCount",
  async ({ datasetId }, thunkAPI) => {
    try {
      const { data } = await client.graphql({ query: getDataset, variables: { id: datasetId } });
      const dataset = data?.getDataset;
      if (!dataset) throw new Error("Dataset not found.");

      const records = await fetchAllDataSetRecords(datasetId);
      const stats = records.length ? statsFromDataSetRecords(records) : await statsFromLegacyStorage(dataset);

      // A failed read is not an empty dataset — writing zeros here used to wipe a healthy dataset's metadata.
      // A dataset that has never held anything is simply confirmed as empty.
      if (!stats) {
        if (!dataset.recordCount && !dataset.fileSize) return dataset;
        throw new Error(
          "No data files found for this dataset, so its stored counts were left unchanged."
        );
      }

      const { data: updateData } = await client.graphql({
        query: updateDataset,
        variables: { input: { id: datasetId, ...stats } },
      });

      return updateData.updateDataset;
    } catch (error) {
      console.error("Error refreshing dataset record count:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Redux slice
const datasetSlice = createSlice({
  name: "datasets",
  initialState,
  reducers: {
    setSelectedDataset: (state, action) => {
      state.selectedDataset = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.loading = "idle";
      state.error = null;
      state.personalDatasets = {};
      state.organizationDatasets = {};
      state.selectedDataset = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch datasets
      .addCase(fetchDatasets.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchDatasets.fulfilled, (state, action) => {
        // Stays pending: the newer call that superseded this one settles the state
        if (action.payload.superseded) return;
        state.loading = "succeeded";
        state.personalDatasets = action.payload.personal;
        state.organizationDatasets = action.payload.organization;
      })
      .addCase(fetchDatasets.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload || "Failed to fetch datasets";
      })

      // Create dataset
      .addCase(createDatasetAction.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(createDatasetAction.fulfilled, (state, action) => {
        state.loading = "succeeded";
        const dataset = action.payload;
        if (dataset.organizationId) {
          state.organizationDatasets[dataset.id] = dataset;
        } else {
          state.personalDatasets[dataset.id] = dataset;
        }
      })
      .addCase(createDatasetAction.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload || "Failed to create dataset";
      })

      // Update dataset
      .addCase(updateDatasetAction.fulfilled, (state, action) => {
        const dataset = action.payload;
        if (dataset.organizationId && state.organizationDatasets[dataset.id]) {
          state.organizationDatasets[dataset.id] = dataset;
        } else if (state.personalDatasets[dataset.id]) {
          state.personalDatasets[dataset.id] = dataset;
        }
      })

      // Delete dataset
      .addCase(deleteDatasetAction.fulfilled, (state, action) => {
        const id = action.payload;
        delete state.personalDatasets[id];
        delete state.organizationDatasets[id];
      })

      // Recheck record count, and a re-read after an upload finished
      .addCase(refreshDatasetRecordCount.fulfilled, (state, action) => {
        const dataset = action.payload;
        if (dataset.organizationId && state.organizationDatasets[dataset.id]) {
          state.organizationDatasets[dataset.id] = dataset;
        } else if (state.personalDatasets[dataset.id]) {
          state.personalDatasets[dataset.id] = dataset;
        }
      })
      .addCase(fetchDatasetById.fulfilled, (state, action) => {
        const dataset = action.payload;
        if (dataset.organizationId && state.organizationDatasets[dataset.id]) {
          state.organizationDatasets[dataset.id] = dataset;
        } else if (state.personalDatasets[dataset.id]) {
          state.personalDatasets[dataset.id] = dataset;
        }
      });
  },
});

export const { setSelectedDataset, clearError, resetState } = datasetSlice.actions;
export default datasetSlice.reducer;
